/**
 * @module data/cases
 *
 * Case studies. Each is structured so the interactive "proof" section can tell
 * the story in three beats — the situation, the intervention, the outcome —
 * with the ADM phases and capabilities that were in play, plus hard numbers.
 */

/**
 * @typedef {Object} CaseMetric
 * @property {string} value
 * @property {string} label
 * @property {string} [detail]
 *
 * @typedef {Object} CaseStudy
 * @property {string}   id
 * @property {string}   client      Anonymised client descriptor.
 * @property {string}   sector
 * @property {string}   size
 * @property {string}   title
 * @property {string}   summary
 * @property {string}   situation
 * @property {string}   intervention
 * @property {string}   outcome
 * @property {CaseMetric[]} metrics
 * @property {string[]} phases      ADM phase ids exercised.
 * @property {string[]} capabilities Capability ids applied.
 * @property {string}   quote
 * @property {string}   attribution
 * @property {string}   accent
 * @property {number}   duration    Months.
 */

/** @type {CaseStudy[]} */
export const CASES = [
  {
    id: "manufacturer",
    client: "European industrial manufacturer",
    sector: "Manufacturing",
    size: "18,000 employees · 9 plants",
    title: "From fourteen planning spreadsheets to one governed capability",
    summary:
      "A manufacturer with a decade of acquisitions had fourteen planning processes and no single version of demand truth. We rebuilt the capability, then put an agent on top of it.",
    situation:
      "Planning was fragmented across nine plants, each with its own spreadsheet logic and no shared definition of demand. Forecast accuracy was unmeasured, so nobody could prove it was bad — and every plant defended its own numbers politically.",
    intervention:
      "Phases B through F: a capability model that made the fragmentation visible, a data architecture with a single demand entity and published contracts, a transition roadmap that kept every plant running while the target was built, and a planning agent that drafts the weekly plan for a human planner to approve.",
    outcome:
      "One planning capability, one demand definition, one governed agent. The planner's week moved from assembling numbers to interrogating them, and for the first time the executive team could compare plants on a shared measure.",
    metrics: [
      { value: "14 → 1", label: "Planning processes", detail: "Consolidated into a single governed capability." },
      { value: "+31%", label: "Forecast accuracy", detail: "Measured against a baseline that previously did not exist." },
      { value: "−64%", label: "Planning effort", detail: "Hours per planning cycle across nine plants." },
      { value: "2.4", label: "Days saved per cycle", detail: "Moved from assembly work to analysis." },
    ],
    phases: ["preliminary", "business", "information-systems", "opportunities", "migration", "implementation-governance"],
    capabilities: ["business-architecture", "data-ai-architecture", "process-intelligence", "governance-risk", "measurement-analytics"],
    quote:
      "They did not sell us a forecasting tool. They made us agree on what demand is, then made the disagreement impossible to hide.",
    attribution: "Chief Supply Chain Officer",
    accent: "#6f9fbe",
    duration: 11,
  },
  {
    id: "bank",
    client: "Nordic retail bank",
    sector: "Financial services",
    size: "6,200 employees · 3 countries",
    title: "Approving a lending copilot the regulator could live with",
    summary:
      "A lending transformation that had stalled on assurance. We made the AI service explainable, evaluable and suspendable — and it cleared review in one cycle.",
    situation:
      "An in-house lending assistant had been in pilot for eighteen months. It performed well, but nobody could explain an individual recommendation to a credit officer, to internal audit, or to the supervisor. So it stayed in pilot.",
    intervention:
      "Phase G-led remediation with Phase C rework: retrieval restricted to entitlement-checked sources, a replayable decision record for every recommendation, a fairness and refusal evaluation suite in the release pipeline, and a named human owner with the authority to suspend the service.",
    outcome:
      "The service went into production with conditions rather than a deferral. Credit officers can now see which evidence drove a recommendation, and the review board has the evidence it needs to renew approval each quarter.",
    metrics: [
      { value: "18 → 3", label: "Months in pilot", detail: "From stalled pilot to conditional production approval." },
      { value: "100%", label: "Decisions replayable", detail: "Sources, model version and rule path recorded per recommendation." },
      { value: "−41%", label: "Time to decision", detail: "For applications routed to assisted review." },
      { value: "6", label: "Evaluation suites", detail: "Accuracy, fairness, refusal, cost, latency, adversarial." },
    ],
    phases: ["information-systems", "technology", "implementation-governance", "change-management"],
    capabilities: ["data-ai-architecture", "responsible-ai", "security-risk", "governance-risk", "application-integration"],
    quote:
      "The question was never whether the model was good. It was whether we could defend one decision to one regulator. Now we can.",
    attribution: "Head of Credit Risk",
    accent: "#5fd4c4",
    duration: 7,
  },
  {
    id: "public-sector",
    client: "National public agency",
    sector: "Public sector",
    size: "24,000 employees · national",
    title: "A citizen-service capability that survived four ministers",
    summary:
      "Four changes of political leadership in five years. The architecture survived all of them because it was traceable to outcomes, not to a minister's preference.",
    situation:
      "A citizen-facing service had been restarted three times, each restart erasing the previous design. Requirements lived in ministerial briefings, and no one could say which capability the investment was meant to create.",
    intervention:
      "Phase A and Preliminary Phase discipline: architecture principles ratified by the permanent administration rather than the political leadership, a requirement traceability matrix from citizen outcome to system change, and gates that made restarting a documented decision with a cost rather than a habit.",
    outcome:
      "Three ministers later, the target architecture is unchanged in substance and the programme has delivered four increments. Restarts stopped because the cost of one became visible.",
    metrics: [
      { value: "3 → 0", label: "Programme restarts", detail: "In the four years since the principles were ratified." },
      { value: "94%", label: "Requirements traced", detail: "From citizen outcome to delivered system change." },
      { value: "4", label: "Increments delivered", detail: "Against a baselined migration plan." },
      { value: "+22pt", label: "Citizen satisfaction", detail: "Measured on the national service index." },
    ],
    phases: ["preliminary", "vision", "business", "migration", "implementation-governance"],
    capabilities: ["operating-model", "governance-risk", "change-adoption", "value-portfolio", "talent-enablement"],
    quote:
      "We stopped arguing about whose vision it was and started arguing about the evidence. The second argument is survivable.",
    attribution: "Director-General, Service Transformation",
    accent: "#d8a657",
    duration: 24,
  },
  {
    id: "insurer",
    client: "Global speciality insurer",
    sector: "Insurance",
    size: "11,500 employees · 14 markets",
    title: "Building an architecture practice, not just an architecture",
    summary:
      "The target design was never the problem. The problem was that nobody inside the company could maintain it after we left. So we made capability transfer the deliverable.",
    situation:
      "A well-funded transformation had produced excellent target-state documents and no ability to keep them current. Architects were outsourced, decisions were undocumented and every change reopened settled questions.",
    intervention:
      "Preliminary Phase capability building: a tailored ADM the client owns, an architecture repository they administer, a pairing model where our architects worked alongside theirs on real decisions, and a certification path that put forty-two internal architects through the method.",
    outcome:
      "Nine months after handover the client had run two full ADM cycles unaided, and had refused three dispensations on the basis of their own baseline.",
    metrics: [
      { value: "42", label: "Architects certified", detail: "Internal staff enabled on the tailored ADM." },
      { value: "2", label: "Cycles run unaided", detail: "Completed after our disengagement." },
      { value: "3", label: "Dispensations refused", detail: "By the client's own architecture board." },
      { value: "−58%", label: "External architecture spend", detail: "Year two against year one." },
    ],
    phases: ["preliminary", "vision", "opportunities", "implementation-governance", "change-management"],
    capabilities: ["talent-enablement", "operating-model", "governance-risk", "business-architecture"],
    quote:
      "The measure of the engagement is that we no longer need them. That was the brief, and they took it literally.",
    attribution: "Group CIO",
    accent: "#a98bc4",
    duration: 15,
  },
];

/**
 * Find a case study by id.
 *
 * @param {string} id
 * @returns {CaseStudy|undefined}
 */
export function getCaseById(id) {
  return CASES.find((entry) => entry.id === id);
}
